/**
 * Sheet — the phone rendering of the column (BRIEF §14): the sheet IS the column, its handle the
 * pill — "the grabber = the pill" (BRIEF.md, Mobile section): `Pill.tsx` renders unconditionally on
 * every breakpoint (`Wall.tsx` never branches it on `isMobile`), so the docked pill's own tap
 * already IS the phone's rising gesture and its Continue ↑ already runs the same scroll-then-arm
 * handler a desktop Continue does (Iteration II §3) — nothing here duplicates that wiring. Rises on
 * tap only, never on a drag (boards ToileMobileTapped / ToileMobileWorking); swipe down on the
 * grabber BELOW (the visual bar inside the already-open sheet) returns to the wall, same as the
 * desktop scroll-return. The escape strip above it dims the wall and closes on tap, matching the
 * desktop scrim.
 *
 * Frame, counter, ratio chip and the cell at work (`WorkingCell.tsx`, while `working`) are the
 * column's, on the sheet's own box — see `Column.tsx`'s header for what each one is. Two
 * differences, both from the box: the frame fits into `frame.sheetFrameBox` (the sheet's content
 * width rather than the column's own bar width, § D1/D5 out of this file's scope — the sheet's frame
 * size does not change), and the chip's pill row is centred across the sheet's width rather than
 * aligned under the chip — under a thumb the sheet's own edges are the alignment, and the
 * bottom-anchored sheet simply grows upward as the row rises from its bar (board `ColumnFurniture`
 * frame 4). The counter stays in the sheet's own flow (unlike the column's beside placement, § D3 —
 * out of scope here too), and the identity slot lives in the bar the same way the column's does
 * (§ D6).
 */
import {useEffect, useId, useRef, useState} from 'react'
import {Link} from 'react-router'
import {MAX_DIRECTION_CHARS, type Identity, type RatioKey} from '@core/wall/contract'
import {Notice} from '@core/ui/Notice'
import {
    chainTravel, fieldPlaceholder, identityArmable, noticeKindFor,
    type Aspect, type ChainStep, type VerbState,
} from './chains'
import {Counter, type PinDoorProps, type PinSlotState} from './Counter'
import {RatioChip, RatioRow} from './RatioFold'
import {WordDoors} from './WordDoors'
import type {Door} from './chains'
import {fitBox, sheetFrameBox, type Box} from './frame'
import {useFocusTrap} from './useFocusTrap'
import {BarNote, BarVerb} from './AllowanceBar'
import {ShareDoor, ShareMenu, useFoldOnOutsidePress, useShareMenuId, type ShareDoorProps} from './ShareDoor'
import {chromeWords, pencilOn, verbWord, type BarAllowance, type HouseTheme} from './theme'
import {WorkingCell, type PreviewFrame} from './WorkingCell'
import styles from './Sheet.module.scss'

export type SheetProps = {
    theme: HouseTheme
    /** Same accessible-name/fallback role as `Column`'s own `rootLabel`. */
    rootLabel: string
    /** Same doors as `Column`'s own `rootDoors` — see its doc for the fallback rule. */
    rootDoors: readonly Door[]
    /** Same door tap as `Column`'s own `onWordTap`. */
    onWordTap: (door: Door) => void
    /** Same door the column's caption carries — see `Column`'s own `rootHref`. */
    rootHref: string | null
    resultUrl: string | null
    working: boolean
    /** Same two paint inputs the column draws for the same row — see `Column`'s own
     *  `queuePlace`/`previewFrame`. */
    queuePlace?: number | null
    previewFrame?: PreviewFrame | null
    isScratch: boolean
    direction: string
    onDirectionChange: (value: string) => void
    ratio: RatioKey
    onRatioChange: (ratio: RatioKey) => void
    onGenerate: () => void
    onClose: () => void
    /** Same word-pick the column draws — see `Column`'s own `verbState`. */
    verbState: VerbState
    /** Same counter the column draws — see `Column`'s own `steps`/`selectedStepId`. */
    steps: readonly ChainStep[]
    selectedStepId: string | null
    onSelectStep: (id: string) => void
    /** Same frame the column draws — see `Column`'s own `aspect` (N14). */
    aspect: Aspect
    viewport: Box
    /** Same line the column carries, scoped the same way — see `Column`'s own `note`. */
    note?: string | null
    /** False while the sign-in card is over the sheet: one layer traps the keyboard at a time. */
    trapFocus?: boolean
    /** Same fold slot the column carries — see `Column`'s own `consentSlot`/`onFoldConsent`. */
    consentSlot?: React.ReactNode
    onFoldConsent?: () => void
    /** Same pin door the column carries — see `Column`'s own `pin`. 44×44 tap target on the phone
     *  (`Counter.module.scss`'s `.door`, unchanged from the column's — one build for both). */
    pin?: PinDoorProps | null
    /** Same pin slot state the column carries — see `Column`'s own `pinSlot`. */
    pinSlot?: PinSlotState
    /** Same Share door the column carries — see `Column`'s own `share`. On the phone the door taps straight
     *  into the system sheet where the picture can be shared; the menu stands in the sheet's flow. */
    share?: ShareDoorProps | null
    /** See `Column`'s own `identity` — same field, same fallback behaviour. */
    identity?: Identity
    /** See `Column`'s own `onIdentityChange`. */
    onIdentityChange?: (identity: Identity) => void
    /** See `Column`'s own `identityOpenSignal` — same external counter, same internal translation
     *  into `IdentityPanelProps.open`. */
    identityOpenSignal?: number
    /** See `Column`'s own `onOpenIntake`. */
    onOpenIntake?: () => void
    /** See `Column`'s own `allowanceBar`. */
    allowanceBar?: BarAllowance | null
    /** See `Column`'s own `locked`. */
    locked?: boolean
    /** See `Column`'s own `hideFrom` — the sheet's equivalent caption is `.cap`. */
    hideFrom?: boolean
}

/**
 * The visual viewport's box, followed live: `position: fixed` anchors to the LAYOUT viewport, and
 * on iOS the on-screen keyboard shrinks only the visual one (and Safari scrolls the layout viewport
 * to reveal a focused field), so a bottom-anchored fixed sheet ends up under the keyboard — its
 * field, the one verb, hidden by the thing typing into it. Operator-observed on iPhone 16,
 * 2026-09-02. The arena is re-laid on the visual viewport's `top`/`height` instead (the
 * `VisualViewport` API's documented "device-fixed" emulation), so the sheet sits on the keyboard's
 * lip and the frame fits above it. `null` where the API is absent — the CSS `inset: 0` then stands.
 */
function useVisualViewportBox(): {top: number; height: number} | null {
    const [box, setBox] = useState<{top: number; height: number} | null>(null)
    useEffect(() => {
        const vv = window.visualViewport
        if (!vv) return
        function read() {
            if (!vv) return
            // Only the keyboard's own shrink is worth following: at full height the CSS box is exact.
            const shrunk = vv.height < window.innerHeight - 1 || vv.offsetTop > 0
            setBox(shrunk ? {top: Math.round(vv.offsetTop), height: Math.round(vv.height)} : null)
        }
        read()
        vv.addEventListener('resize', read)
        vv.addEventListener('scroll', read)
        return () => {
            vv.removeEventListener('resize', read)
            vv.removeEventListener('scroll', read)
        }
    }, [])
    return box
}

export function Sheet({
    theme, rootLabel, rootDoors, onWordTap, rootHref, resultUrl, working, queuePlace, previewFrame, isScratch,
    direction, onDirectionChange, ratio, onRatioChange, onGenerate, onClose, verbState,
    steps, selectedStepId, onSelectStep, aspect, viewport, note,
    trapFocus = true, consentSlot = null, onFoldConsent, pin = null, pinSlot, share = null,
    identity, onIdentityChange, identityOpenSignal = 0, onOpenIntake, allowanceBar = null, locked = false, hideFrom = false,
}: SheetProps) {
    // Same disarm rule as `Column` — see its own comment.
    const armable = identityArmable(identity)
    // Same identity preview as `Column` (plan `2026_09_18_armed_frame_budget.md` § D6) — see its own
    // comment.
    const [identityPreviewUrl, setIdentityPreviewUrl] = useState<string | null>(null)
    const vvBox = useVisualViewportBox()
    // The frame fits above the keyboard when there is one: the visual height, not the layout's.
    const fitted = fitBox(aspect, sheetFrameBox(vvBox ? {width: viewport.width, height: vvBox.height} : viewport))
    const frame = {width: fitted.width, height: fitted.height}
    // C2: same placeholder rule the column draws — see `Column`'s own comment.
    const displayedStep = chainTravel(steps, selectedStepId).step
    const chrome = chromeWords(theme)
    const placeholderText = fieldPlaceholder(displayedStep, theme.verbs.placeholder)
    const [ratioOpen, setRatioOpen] = useState(false)
    const ratioRowId = useId()
    // The Share menu, as in `Column` — the same layering, in the sheet's flow.
    const [shareOpen, setShareOpen] = useState(false)
    const shareMenuId = useShareMenuId()
    const shareDrawn = share !== null && !identityPreviewUrl && !working && !(isScratch && !resultUrl)
    const shareMenuOpen = shareDrawn && shareOpen
    const scrimRef = useRef<HTMLDivElement>(null)
    const scrimPressFoldedRef = useRef(false)
    useFoldOnOutsidePress({
        open: shareMenuOpen, scrimRef, scrimFolded: scrimPressFoldedRef, fold: () => setShareOpen(false),
    })
    // One fold at a time — `Column` states the whole rule.
    useEffect(() => {
        if (consentSlot) {
            setRatioOpen(false)
            setShareOpen(false)
        }
    }, [consentSlot])
    useEffect(() => {
        if (ratioOpen) setShareOpen(false)
    }, [ratioOpen])
    // N8, as in `Column` — but focus opens on the sheet's own heading rather than its field: the
    // phone keyboard rising unasked would cover the sheet the tap just raised.
    const arenaRef = useRef<HTMLDivElement>(null)
    const capRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    // The identity strip's open/closed state and the pencil's own field — same lifted state
    // `Column.tsx` carries, see its own comments for the full rationale of each.
    const [identityOpen, setIdentityOpen] = useState(false)
    const lastIdentitySignal = useRef(identityOpenSignal)
    useEffect(() => {
        if (identityOpenSignal !== lastIdentitySignal.current) {
            lastIdentitySignal.current = identityOpenSignal
            setIdentityOpen(true)
        }
    }, [identityOpenSignal])
    // One call, one placement — see `Column.tsx`'s own comment for the full rationale.
    const [barHeld, setBarHeld] = useState(false)
    const identityContent = theme.identityPanel && identity && onIdentityChange
        ? theme.identityPanel({
              identity, onIdentityChange, open: identityOpen, onOpenChange: setIdentityOpen,
              onPreview: setIdentityPreviewUrl, onOpenIntake, onHoldBar: setBarHeld,
          })
        : null
    const identityPanelOpen = Boolean(theme.identityPanel) && (!armable || identityOpen)

    const [fieldOpen, setFieldOpen] = useState(() => Boolean(theme.pencil) && direction.trim().length > 0)
    const wasFieldOpen = useRef(fieldOpen)
    useEffect(() => {
        // Unlike the sheet's own MOUNT focus (below, always `capRef` — the keyboard never rises
        // unasked), a pencil tap IS the deliberate gesture the phone keyboard is allowed to follow.
        if (theme.pencil && fieldOpen && !wasFieldOpen.current) {
            inputRef.current?.focus({preventScroll: true})
        }
        wasFieldOpen.current = fieldOpen
    }, [theme.pencil, fieldOpen])

    function togglePencil() {
        if (!fieldOpen) {
            setFieldOpen(true)
            return
        }
        if (direction.trim() === '') setFieldOpen(false)
    }

    // Escape folds the consent card, then an open ratio row, then the pencil's own empty field,
    // before it closes the sheet — `Column` states the whole rule.
    useFocusTrap({
        active: trapFocus,
        containerRef: arenaRef,
        onEscape: () => {
            if (consentSlot) {
                onFoldConsent?.()
                return
            }
            if (shareMenuOpen) {
                setShareOpen(false)
                return
            }
            if (ratioOpen) {
                setRatioOpen(false)
                return
            }
            // The muse strip, if open — same rule as `Column`'s own comment.
            if (identityOpen) {
                setIdentityOpen(false)
                return
            }
            if (theme.pencil && fieldOpen && direction.trim() === '') {
                setFieldOpen(false)
                return
            }
            onClose()
        },
        // Always the cap, pencil or not — the sheet never summons the keyboard on open (this
        // file's own header doc); a pencil tap is the one deliberate gesture allowed to.
        initialFocusRef: capRef,
    })

    return (
        <div
            className={styles.arena}
            ref={arenaRef}
            role="dialog"
            aria-modal="true"
            aria-label={rootLabel}
            style={vvBox ? {top: vvBox.top, height: vvBox.height, bottom: 'auto'} : undefined}
        >
            <div
                ref={scrimRef}
                className={styles.scrim}
                onClick={() => {
                    if (scrimPressFoldedRef.current) {
                        scrimPressFoldedRef.current = false
                        return
                    }
                    onClose()
                }}
            />
            <div className={styles.esc}>
                <span>{chrome.back.lead}</span>
                <span>{chrome.back.phone}</span>
            </div>

            {/* `data-wall-sheet` tells the wall's return listener (`Wall.tsx`) which touches begin on
                the sheet: there, only a DOWNWARD drag is the return (BRIEF §14, "swipe down
                returns") — anywhere on it, the grabber included, one mechanism. On the scrim any
                vertical drag is the scroll that returns, as on desktop. The arena's
                `touch-action: none` (Sheet.module.scss) is what keeps the wall from moving under
                the finger in the meantime. */}
            <div className={styles.sheet} data-wall-sheet="true">
                <div className={styles.grabber} />

                {identityPreviewUrl ? (
                    <div className={styles.result} style={frame}>
                        <img src={identityPreviewUrl} alt="" />
                    </div>
                ) : isScratch && !resultUrl ? (
                    <div className={`${styles.result} ${styles.resultScratch}`} style={frame}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 3 a2.83 2.83 0 0 1 4 4 L7.5 20.5 L2 22 L3.5 16.5 Z" />
                        </svg>
                        <span>{direction || theme.verbs.placeholder.onBlank}</span>
                    </div>
                ) : (
                    <div className={styles.result} style={frame}>
                        {working ? (
                            <WorkingCell queuePlace={queuePlace} previewFrame={previewFrame} />
                        ) : resultUrl ? (
                            <img src={resultUrl} alt="" />
                        ) : null}
                        {/* The counter lives INSIDE the picture on the phone (2026-09-18,
                            `Counter.tsx`'s own header doc) — no room stands beside it the way
                            `Column.tsx`'s `.frameWrap` gives the desktop split layout. `.result` is
                            already `position: relative; overflow: hidden` (Sheet.module.scss), so the
                            zones and the pill position against it directly; both stand above
                            `WorkingCell` (`z-index: 1` on the pill, the zones need none — `WorkingCell`
                            paints no photograph beneath them to obscure). Neither the scratch branch
                            nor the identity-preview branch above draws this: no chain step is on
                            screen in either. */}
                        <Counter
                            layout="inside"
                            steps={steps} selectedStepId={selectedStepId} onSelectStep={onSelectStep}
                            pin={pin} pinSlot={pinSlot} pinWords={chrome.pin}
                        />
                        {shareDrawn && share && (
                            <ShareDoor
                                door={share} surface="sheet" open={shareMenuOpen}
                                onOpenChange={(open) => {
                                    setShareOpen(open)
                                    if (open) setRatioOpen(false)
                                }}
                                menuId={shareMenuId}
                            />
                        )}
                    </div>
                )}

                {/* The cap stays the sheet's opening focus (a plain box, `tabIndex={-1}`) — the doors
                    inside it are their own tab stops, so opening never lands focus ON one of them.
                    `hideFrom` (P5) drops it whole — a visitor's locked sheet has no estate text to
                    name; the trap's own `focusables()[0]` fallback stands in for the initial focus
                    that would otherwise land here. */}
                {!hideFrom && (
                    <div className={styles.cap} ref={capRef} tabIndex={-1}>
                        {rootDoors.length > 0 ? (
                            <WordDoors doors={rootDoors} onTap={onWordTap} href={rootHref} size="caption" />
                        ) : rootHref ? (
                            <Link to={rootHref} className={styles.plain}>{rootLabel} ↗</Link>
                        ) : (
                            <span className={styles.plain}>{rootLabel}</span>
                        )}
                    </div>
                )}

                {shareMenuOpen && share && (
                    <ShareMenu door={share} surface="sheet" onOpenChange={setShareOpen} menuId={shareMenuId} variant="flow" />
                )}

                {consentSlot ? (
                    <div className={styles.consentSlot} data-wall-return-exempt="true">{consentSlot}</div>
                ) : (
                    ratioOpen && (
                        <RatioRow
                            id={ratioRowId}
                            keys={theme.ratioKeys}
                            ratio={ratio}
                            onPick={(key) => {
                                onRatioChange(key)
                                setRatioOpen(false)
                            }}
                            className={styles.ratioRow}
                        />
                    )
                )}

                <div className={styles.bar}>
                    {identityPanelOpen ? (
                        <span className={styles.idOpen} data-wall-return-exempt="true">{identityContent}</span>
                    ) : theme.pencil && (!fieldOpen || allowanceBar?.verb) ? (
                        <span className={styles.idChip} data-wall-return-exempt="true">
                            {identityContent}
                            {allowanceBar && <BarNote bar={allowanceBar} />}
                        </span>
                    ) : (
                        <span className={styles.slot} data-wall-return-exempt="true">
                            <input
                                ref={inputRef}
                                type="text"
                                value={direction}
                                onChange={(e) => onDirectionChange(e.target.value)}
                                placeholder={placeholderText}
                                className={styles.input}
                                maxLength={MAX_DIRECTION_CHARS}
                                aria-label={placeholderText}
                                onBlur={
                                    theme.pencil
                                        ? () => {
                                              if (direction.trim() === '') setFieldOpen(false)
                                          }
                                        : undefined
                                }
                                onKeyDown={(e) => {
                                    // The same cancel `Column` makes, for the same reason — stated
                                    // there in full: an uncancelled Enter also runs its default action,
                                    // on whatever holds focus by the time the browser gets to it.
                                    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return
                                    e.preventDefault()
                                    // Museless-disarmed (Ovid) — see `Column`'s own comment.
                                    if (armable) onGenerate()
                                }}
                            />
                        </span>
                    )}
                    {!identityPanelOpen && theme.hasRatioPicker && (
                        <RatioChip
                            rowId={ratioRowId}
                            ratio={ratio}
                            open={ratioOpen}
                            onToggle={() => setRatioOpen(!ratioOpen)}
                        />
                    )}
                    {!identityPanelOpen && !allowanceBar?.verb && pencilOn(theme, identity) && (
                        <button
                            type="button"
                            className={styles.pencil}
                            aria-label={chrome.pencil}
                            // See `Column`'s own comment: not a toggle in locked mode (P5).
                            aria-pressed={locked ? undefined : fieldOpen}
                            onClick={locked ? onGenerate : togglePencil}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 3 a2.83 2.83 0 0 1 4 4 L7.5 20.5 L2 22 L3.5 16.5 Z" />
                            </svg>
                        </button>
                    )}
                    {!barHeld && (allowanceBar?.verb ? (
                        <BarVerb verb={allowanceBar.verb} className={styles.verb} />
                    ) : (
                        <button type="button" className={styles.verb} onClick={onGenerate} disabled={!armable}>
                            {verbWord(theme, verbState, identity, working)}
                        </button>
                    ))}
                </div>

                {note && <Notice kind={noticeKindFor(note)} className={styles.note} ttlMs={0}>{note}</Notice>}
            </div>
        </div>
    )
}
